import { FC, memo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SvgIconComponent } from "@mui/icons-material";

export interface EmptyStateProps {
  /** MUI Icon component to display */
  icon: SvgIconComponent;
  /** Main title text */
  title: string;
  /** Description text below the title */
  description: string;
  /** Visual variant - controls icon and title colors */
  variant?: "default" | "error";
}

/**
 * Reusable empty state component for displaying placeholder content
 * when there's no data to show or an error occurred.
 */
export const EmptyState: FC<EmptyStateProps> = memo(function EmptyState({
  icon: Icon,
  title,
  description,
  variant = "default",
}) {
  const isError = variant === "error";

  return (
    <Box
      data-testid={`empty-state-${variant}`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Stack spacing={2} alignItems="center" maxWidth={400} textAlign="center">
        <Icon
          sx={{
            fontSize: 64,
            color: isError ? "error.main" : "text.secondary",
            opacity: isError ? 0.7 : 0.5,
          }}
        />
        <Typography
          variant="h5"
          color={isError ? "error.main" : "text.primary"}
          fontWeight={500}
        >
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </Stack>
    </Box>
  );
});
